﻿import apiClient from './api.service';
import type {
  AnalyticsOption,
  AnalyticsQuestion,
  CursorPage,
  DownloadLocation,
  ExportDefinition,
  ExportSummary,
  OptionListParams,
  QueryDefinition,
  QueryResponse,
  QueryResult,
  RankingParams,
  SavedView,
  SavedViewInput,
  VersionDetail,
  VersionListParams,
  ComparisonRequest,
  ComparisonResult,
  VersionSummary,
} from '../types/survey-analytics.types';

type RequestOptions = { signal?: AbortSignal };

export const surveyAnalyticsService = {
  async listVersions(surveyId: string, params: VersionListParams = {}, options: RequestOptions = {}): Promise<CursorPage<VersionSummary>> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions`, { params, signal: options.signal });
    return response.data;
  },

  async getVersion(surveyId: string, versionId: string, options: RequestOptions = {}): Promise<VersionDetail> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}`, { signal: options.signal });
    return response.data;
  },

  async listQuestions(surveyId: string, versionId: string, params = {}, options: RequestOptions = {}): Promise<CursorPage<AnalyticsQuestion>> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/questions`, { params, signal: options.signal });
    return response.data;
  },

  async listOptions(surveyId: string, versionId: string, questionId: string, params: OptionListParams = {}, options: RequestOptions = {}): Promise<CursorPage<AnalyticsOption>> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/questions/${questionId}/options`, { params, signal: options.signal });
    return response.data;
  },

  async getRanking(surveyId: string, versionId: string, questionId: string, params: RankingParams = {}, options: RequestOptions = {}): Promise<QueryResult> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/questions/${questionId}/ranking`, { params: { pageSize: 20, ...params }, signal: options.signal });
    return response.data;
  },

  async createQuery(surveyId: string, versionId: string, definition: QueryDefinition, options: RequestOptions = {}): Promise<QueryResponse> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/analytics/versions/${versionId}/queries`, definition, { signal: options.signal });
    return response.data;
  },

  async getQuery(surveyId: string, versionId: string, queryId: string, options: RequestOptions = {}): Promise<QueryResponse> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/queries/${queryId}`, { signal: options.signal });
    return response.data;
  },
  async createView(surveyId: string, versionId: string, input: SavedViewInput, options: RequestOptions = {}): Promise<SavedView> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/analytics/versions/${versionId}/views`, input, { signal: options.signal });
    return response.data;
  },

  async listViews(surveyId: string, versionId: string, params = {}, options: RequestOptions = {}): Promise<CursorPage<SavedView>> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/views`, { params, signal: options.signal });
    return response.data;
  },

  async deleteView(surveyId: string, versionId: string, viewId: string, options: RequestOptions = {}): Promise<void> {
    await apiClient.delete(`/api/surveys/${surveyId}/analytics/versions/${versionId}/views/${viewId}`, { signal: options.signal });
  },

  async createExport(surveyId: string, versionId: string, definition: ExportDefinition, options: RequestOptions = {}): Promise<ExportSummary> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/analytics/versions/${versionId}/exports`, { definition, format: 'CSV' }, { signal: options.signal });
    return response.data;
  },

  async getExport(surveyId: string, versionId: string, exportId: string, options: RequestOptions = {}): Promise<ExportSummary> {
    const response = await apiClient.get(`/api/surveys/${surveyId}/analytics/versions/${versionId}/exports/${exportId}`, { signal: options.signal });
    return response.data;
  },

  async createExportDownloadUrl(surveyId: string, versionId: string, exportId: string, options: RequestOptions = {}): Promise<DownloadLocation> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/analytics/versions/${versionId}/exports/${exportId}/download-url`, {}, { signal: options.signal });
    return response.data;
  },
  async comparePreviews(surveyId: string, input: ComparisonRequest, options: RequestOptions = {}): Promise<ComparisonResult> {
    const response = await apiClient.post(`/api/surveys/${surveyId}/analytics/comparisons`, input, { signal: options.signal });
    return response.data;
  },
};

export default surveyAnalyticsService;





